import { computeLineDiscountAmount, parsePositiveRate, parseWholeQty } from '../utils';

function formatDecimal(value, fallback = '0') {
  if (value === undefined || value === null || value === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : fallback;
}

function formatQtyString(value, fallback = '0') {
  if (value === undefined || value === null || value === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : fallback;
}

/** Discount % is always a positive magnitude (API may return -1 for 1%). */
export function normalizeDiscountPercent(value) {
  const n = Math.abs(Number(value) || 0);
  return Number.isFinite(n) ? Math.min(n, 100) : 0;
}

/** Tax % is always non-negative and applied on the post-discount base. */
export function normalizeTaxPercent(value) {
  const n = Number(value) || 0;
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(n, 100);
}

function poItemKey(item) {
  return item?.purchase_order_item_id || item?.po_item_id || item?.id || '';
}

/** Resolve the PO line that matches a GRN item (by link id, then item_id). */
export function findPoItemForGrnLine(grnItem, poItems = []) {
  const linkId = grnItem?.purchase_order_item_id || grnItem?.po_item_id;
  if (linkId) {
    const match = poItems.find((pi) => {
      const keys = [pi.purchase_order_item_id, pi.po_item_id, pi.id].filter(Boolean);
      return keys.some((k) => String(k) === String(linkId));
    });
    if (match) return match;
  }
  if (grnItem?.item_id) {
    return poItems.find((pi) => String(pi.item_id) === String(grnItem.item_id)) || null;
  }
  return null;
}

function resolvePurchaseOrderItemId(grnItem, poItem) {
  return poItemKey(poItem)
    || grnItem?.purchase_order_item_id
    || grnItem?.po_item_id
    || '';
}

function resolveUnitRate(grnItem, poItem) {
  return grnItem?.purchase_rate
    ?? grnItem?.unit_rate
    ?? poItem?.unit_rate
    ?? poItem?.purchase_rate
    ?? 0;
}

function resolveTaxPercent(grnItem, poItem) {
  return grnItem?.tax_percent
    ?? grnItem?.gst_percent
    ?? poItem?.tax_percent
    ?? poItem?.gst_percent
    ?? 0;
}

function resolveDiscountPercent(grnItem, poItem) {
  return grnItem?.discount_percent ?? poItem?.discount_percent ?? 0;
}

/**
 * Build one editable invoice line from GRN + PO API records.
 * GRN is the source of truth for qty/rate; PO fills missing links and uom.
 */
export function mapGrnItemToInvoiceLine(grnItem, poItem, itemLabel) {
  const qty = Math.floor(Number(grnItem.accepted_quantity ?? grnItem.received_quantity) || 0);
  const disc = normalizeDiscountPercent(resolveDiscountPercent(grnItem, poItem));
  const tax = normalizeTaxPercent(resolveTaxPercent(grnItem, poItem));
  const rate = resolveUnitRate(grnItem, poItem);

  return {
    grn_item_id: grnItem.grn_item_id || grnItem.id,
    purchase_order_item_id: resolvePurchaseOrderItemId(grnItem, poItem),
    item_id: grnItem.item_id,
    item_name: itemLabel,
    uom_id: grnItem.uom_id || poItem?.uom_id || '',
    grnQty: qty,
    invoiced_quantity: String(qty),
    unit_rate: String(rate),
    discount_percent: String(disc),
    tax_percent: String(tax),
  };
}

/** Per-line financials: discount subtracts, tax adds on discounted base. */
export function computeLineInvoiceFinancials(qty, rate, discPct, taxPct) {
  const q = Number(qty) || 0;
  const r = Number(rate) || 0;
  const disc = normalizeDiscountPercent(discPct);
  const tax = normalizeTaxPercent(taxPct);
  const gross = q * r;
  const discAmt = computeLineDiscountAmount(q, r, disc);
  const taxable = gross - discAmt;
  const taxAmt = taxable * (tax / 100);
  return {
    gross,
    discAmt,
    taxAmt,
    lineTotal: taxable + taxAmt,
  };
}

/** Map form state to POST /purchase-invoices body (string decimals per API contract). */
export function buildCreatePurchaseInvoiceBody({
  invoiceNumber,
  po,
  grn,
  selectedPoId,
  selectedGrnId,
  invoiceDate,
  dueDate,
  paymentTerms,
  lines,
}) {
  const body = {
    invoice_number: invoiceNumber,
    vendor_id: po.vendor_id,
    purchase_order_id: selectedPoId,
    grn_id: selectedGrnId || null,
    invoice_date: invoiceDate,
    due_date: dueDate,
    payment_terms: paymentTerms,
    lines: lines.map((l) => ({
      purchase_order_item_id: l.purchase_order_item_id,
      grn_item_id: l.grn_item_id,
      item_id: l.item_id,
      uom_id: l.uom_id,
      invoiced_quantity: formatQtyString(l.invoiced_quantity),
      unit_rate: formatDecimal(l.unit_rate),
      discount_percent: formatDecimal(normalizeDiscountPercent(l.discount_percent), '0'),
      tax_percent: formatDecimal(normalizeTaxPercent(l.tax_percent), '0'),
    })),
  };

  const tenant_id = po?.tenant_id || grn?.tenant_id;
  const hospital_id = po?.hospital_id || grn?.hospital_id;
  if (tenant_id) body.tenant_id = tenant_id;
  if (hospital_id) body.hospital_id = hospital_id;

  return body;
}

/** Surface 422 validation, 5xx, and network errors for toast display. */
export function formatCreateInvoiceError(err, fallback = 'Failed to create purchase invoice') {
  if (err?.status === 422) {
    const detail = err.apiResponse?.detail;
    if (Array.isArray(detail)) {
      const msgs = detail.map((d) => d.msg || d.message).filter(Boolean);
      if (msgs.length) return msgs.join('; ');
    }
    if (typeof detail === 'string') return detail;
    // The EMR gateway passes the service's reason on as `message`, not `detail`.
    if (err.apiResponse?.message) return err.apiResponse.message;
    return 'Validation failed — check invoice fields and line quantities';
  }
  if (err?.status >= 500) {
    return err?.message
      || err?.apiResponse?.message
      || err?.apiResponse?.error?.detail
      || 'Server error — please try again later';
  }
  if (!err?.status && err?.message === 'Network Error') return 'Network error — check your connection';
  return err?.message || fallback;
}

/** tenant_id/hospital_id captured on the fetched invoice — thread through match/post/reverse calls. */
export function extractInvoiceScope(invoice = {}) {
  const scope = {};
  if (invoice.tenant_id) scope.tenant_id = invoice.tenant_id;
  if (invoice.hospital_id) scope.hospital_id = invoice.hospital_id;
  return scope;
}

/** Invoice line financial rollup — gross, discount (subtract), tax (add on net). */
export function computeInvoiceFinancials(lines = []) {
  let subtotal = 0;
  let totalDiscount = 0;
  let totalTax = 0;

  for (const line of lines) {
    const { gross, discAmt, taxAmt } = computeLineInvoiceFinancials(
      line.invoiced_quantity,
      line.unit_rate,
      line.discount_percent,
      line.tax_percent,
    );
    subtotal += gross;
    totalDiscount += discAmt;
    totalTax += taxAmt;
  }

  return {
    subtotal,
    totalDiscount,
    totalTax,
    grandTotal: subtotal - totalDiscount + totalTax,
  };
}

export function invoiceLineTotal(line) {
  return computeLineInvoiceFinancials(
    line.invoiced_quantity,
    line.unit_rate,
    line.discount_percent,
    line.tax_percent,
  ).lineTotal;
}

export function normalizeInvoiceLines(invoice = {}) {
  const raw = invoice?.lines ?? invoice?.items ?? [];
  return Array.isArray(raw) ? raw : [];
}

export function parseInvoiceAmount(value) {
  if (value === undefined || value === null || value === '') return null;
  const cleaned = String(value).replace(/^\+/, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** Header-level checks before create. */
export function validateInvoiceHeader({
  po,
  grn,
  invoiceNumber,
  invoiceDate,
  dueDate,
  paymentTerms,
}) {
  if (!invoiceNumber?.trim()) return 'Invoice number is required';
  if (!po) return 'Select a purchase order first';
  if (!po.vendor_id) return 'Purchase order is missing a vendor';
  if (!grn) return 'Select a source GRN first';

  const grnStatus = grn.posting_status || grn.status;
  if (grnStatus && grnStatus !== 'POSTED_TO_STOCK') {
    return 'Invoice can only be created against a GRN posted to stock';
  }

  if (!invoiceDate) return 'Invoice date is required';
  if (!dueDate) return 'Due date is required';
  if (dueDate < invoiceDate) return 'Due date cannot be before invoice date';
  if (!paymentTerms?.trim()) return 'Payment terms are required';

  return null;
}

/** Returns an error message string, or null if every line is postable. */
export function validateInvoiceLines(lines = []) {
  if (!lines.length) return 'Select a GRN with at least one receivable line item';

  for (const line of lines) {
    const label = line.item_name || 'A line item';
    const qty = parseWholeQty(line.invoiced_quantity);
    if (!qty) return `${label}: invoiced quantity must be a whole number greater than 0`;
    if (line.grnQty != null && qty > line.grnQty) {
      return `${label}: invoiced quantity cannot exceed the GRN received quantity (${line.grnQty})`;
    }
    if (parsePositiveRate(line.unit_rate) == null) {
      return `${label}: unit rate must be greater than 0`;
    }
    if (!line.purchase_order_item_id) {
      return `${label}: missing purchase order line link — reload the GRN and try again`;
    }
    if (!line.grn_item_id) {
      return `${label}: missing GRN line link — reload the GRN and try again`;
    }
    if (!line.item_id) return `${label}: missing item reference`;
    if (!line.uom_id) return `${label}: missing unit of measure — check the PO/GRN line`;
    if (normalizeDiscountPercent(line.discount_percent) > 100) {
      return `${label}: discount cannot exceed 100%`;
    }
    if (normalizeTaxPercent(line.tax_percent) > 100) {
      return `${label}: tax cannot exceed 100%`;
    }
  }
  return null;
}

/** Full pre-submit validation for the create-invoice form. */
export function validateCreateInvoice({
  po,
  grn,
  invoiceNumber,
  invoiceDate,
  dueDate,
  paymentTerms,
  lines,
}) {
  return validateInvoiceHeader({
    po, grn, invoiceNumber, invoiceDate, dueDate, paymentTerms,
  }) || validateInvoiceLines(lines);
}

/** Variance override justification — recorded in the finance audit trail, so it must say something. */
export const OVERRIDE_REASON_MIN_LENGTH = 10;

export function validateOverrideReason(value) {
  const trimmed = (value || '').trim();
  if (!trimmed) return 'Override justification is required';
  if (trimmed.length < OVERRIDE_REASON_MIN_LENGTH) {
    return `Override justification must be at least ${OVERRIDE_REASON_MIN_LENGTH} characters`;
  }
  return null;
}

/** Match tolerance for 3-way match dialog (0–100). */
export function validateMatchTolerance(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0 || n > 100) {
    return 'Tolerance must be between 0 and 100';
  }
  return null;
}

/**
 * Compute per-line variance between an invoice line, its PO source, and its GRN source.
 * Every input may be null/undefined — returns null deltas when comparison is impossible.
 */
export function computeLineVariance(invoiceLine, poItem, grnItem) {
  const invoiceQty = invoiceLine?.invoiced_quantity != null
    ? Number(invoiceLine.invoiced_quantity) : null;
  const invoiceRate = invoiceLine?.unit_rate != null
    ? Number(invoiceLine.unit_rate) : null;

  const poRate = poItem?.unit_rate != null
    ? Number(poItem.unit_rate)
    : (poItem?.purchase_rate != null ? Number(poItem.purchase_rate) : null);

  const grnQty = grnItem?.accepted_quantity != null
    ? Number(grnItem.accepted_quantity)
    : (grnItem?.received_quantity != null ? Number(grnItem.received_quantity) : null);

  const qtyDelta = invoiceQty != null && grnQty != null ? invoiceQty - grnQty : null;
  const rateDelta = invoiceRate != null && poRate != null ? invoiceRate - poRate : null;

  return {
    grnQty,
    poRate,
    invoiceQty,
    invoiceRate,
    qtyDelta,
    rateDelta,
    hasQtyVariance: qtyDelta != null && Math.abs(qtyDelta) > 0.001,
    hasRateVariance: rateDelta != null && Math.abs(rateDelta) > 0.001,
  };
}
